import { defineStore } from 'pinia';
import { setMeta } from "~/utils/meta";
import { ProductCollectionPageType } from "~/types/shop/ProductCollectionPageType";
import * as qs from "qs";
import { ProductCategoryEntity } from "~/entities/shop/ProductCategoryEntity";
import { ProductPreviewEntity } from "~/entities/shop/ProductPreviewEntity";

export const useProductCollectionPageStore = defineStore({
    id: 'useProductCollectionPageStore',
    state: (): ProductCollectionPageType => ({
        category: undefined,
        list: undefined,
        pagination: undefined,
        error: undefined,
        skeleton: true,
    }),
    getters: {},
    actions: {
        clear() {
            this.category = undefined;
            this.list = undefined;
            this.pagination = undefined;
            this.error = undefined;
            this.skeleton = true;
        },

        enableSkeleton() {
            this.skeleton = true;
        },

        async fetchCategory(categorySlug: string) {
            try {
                const query = {
                    fields: ['title', 'text_before_content', 'text_after_content'],
                    populate: {
                        seo: {
                            populate: ['metaImage'],
                        },
                    },
                    filters: {
                        slug: {
                            $eq: categorySlug,
                        },
                    },
                };
                const response = await this.$useNuxtApp.axios.get(`/product-categories?${qs.stringify(query, {
                    encodeValuesOnly: true
                })}`);
                if (response?.data?.data === undefined || !response?.data?.data.length || this.error !== undefined) {
                    this.category = undefined;
                    this.error = createError({statusCode: 404, statusMessage: 'Page Not Found'});
                    return false;
                }

                const data = response.data.data[0];
                this.category = ProductCategoryEntity(data);
                if (data?.attributes?.seo) {
                    setMeta(data?.attributes?.seo, this.$useRuntimeConfig, this.$useRoute);
                }
            } catch (error) {
                console.log(error);
                this.error = createError({ statusCode: 500, statusMessage: 'Internal error' });
            }

            return true;
        },

        async fetchList(page: number, categorySlug?: string) {
            const pageSize = 24;
            this.enableSkeleton();

            try {
                const query = {
                    fields: ['slug', 'title', 'sub_title', 'slug', 'price', 'old_price', 'buy_url'],
                    sort: ['publishedAt:desc'],
                    populate: ['images'],
                    pagination: {
                        page,
                        pageSize,
                    },
                    filters: {},
                };

                if (categorySlug !== undefined) {
                    query.filters = {
                        product_category: {
                            slug: {
                                $eq: categorySlug,
                            },
                        },
                    };
                }

                const response = await this.$useNuxtApp.axios.get(`/products?${qs.stringify(query, {
                    encodeValuesOnly: true
                })}`);
                if (response?.data?.data === undefined || this.error !== undefined) {
                    this.error = createError({statusCode: 404, statusMessage: 'Page Not Found'});
                    this.pagination = undefined;
                    this.skeleton = false;
                    return false;
                }

                if (!response?.data?.data.length) {
                    this.list = [];
                    this.skeleton = false;
                    this.pagination = undefined;
                    return true;
                }

                this.list = response?.data?.data.map((item) => ProductPreviewEntity(item, this.$useRuntimeConfig));
                this.pagination = {
                    page: response?.data?.meta?.pagination?.page,
                    pageCount: response?.data?.meta?.pagination?.pageCount,
                };
                this.skeleton = false;
                this.error = undefined;
            } catch (error) {
                this.error = createError({ statusCode: 500, statusMessage: 'Internal error' });
                return false;
            }

            return true;
        }
    },
});
